import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { SupportedCurrency } from '../../../../../domain/currency';
import { CombinedDailyProfitStats } from '../../../../../domain/stats';
import { getPolishMonthName } from '../../../../../utils/polishMonthNames';

ChartJS.register(...registerables);

type SingleMonthCumulativeProfitStatsProps = {
    singleMonthStats: CombinedDailyProfitStats;
    selectedCurrency: SupportedCurrency;
    month: number;
    year: number;
}

export const SingleMonthCumulativeProfitStats = ({
    singleMonthStats,
    selectedCurrency,
    month,
    year,
 }: SingleMonthCumulativeProfitStatsProps) => {
    const statsForSelectedCurrency = singleMonthStats[selectedCurrency];

    if (!statsForSelectedCurrency) {
        return null;
    }

    const {
        daysInSelectedMonth: numberOfDaysInSelectedMonth,
        profits,
        cumulativeProfits,
        minProfit,
        maxProfit,
    } = statsForSelectedCurrency;

    const daysInSelectedMonth = Array.from(Array(numberOfDaysInSelectedMonth).keys()).map(day => day + 1);
    const barChartMin = Number((Math.floor(minProfit * 1.3)).toFixed(2));
    const barChartMax = Number((Math.floor(maxProfit * 1.3)).toFixed(2));

    // Prepare chart data
    const chartData = {
        labels: daysInSelectedMonth.map((day) => day.toString()), // Days of the month as x-axis labels
        datasets: [
            {
                type: 'bar', // Bar chart for daily profits
                label: `Marża dzienna (${selectedCurrency})`,
                data: profits.map(({ value }) => value),
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                yAxisID: 'yBar', // Assign to the second Y-axis
            },
            {
                type: 'line', // Line chart for cumulative profits
                label: `Marża skumulowana (${selectedCurrency})`,
                data: cumulativeProfits,
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 2,
                tension: 0.2, // Smooth curves in the line
                fill: true, // Fill area under the line
                yAxisID: 'yLine', // Assign to the primary Y-axis
            },
        ],
    };

    const options = {
        responsive: true,
        animation: false,
        plugins: {
            tooltip: {
                callbacks: {
                    title: function (context: any) {
                        const monthName = getPolishMonthName(month);
                        return `${context[0].label} ${monthName}`;
                    },
                    label: function (context: any) {
                        // Formatter for tooltips with proper currency formatting
                        const labelPrefix = context.dataset.type === 'line' ? 'Marża skumulowana' : 'Marża dzienna';
                        return `${labelPrefix}: ${context.raw.toLocaleString('pl-PL', {
                            style: 'currency',
                            currency: selectedCurrency,
                        })}`;
                    },
                },
            },
            legend: {
                position: 'top', // Position the legend on top
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: `${getPolishMonthName(month)} ${year}`,
                },
            },
            yLine: {
                beginAtZero: true, // Ensure the y-axis for cumulative profits starts at 0
                position: 'right', // Position this Y-axis on the right
                title: {
                    display: true,
                    text: `Marża skumulowana (${selectedCurrency})`,
                },
                ticks: {
                    callback: (value: any) => `${value} ${selectedCurrency}`, // Format y-axis values
                },
            },
            yBar: {
                beginAtZero: barChartMin >= 0, // If there's no negative profit, start at 0
                position: 'left', // Position this Y-axis on the left
                title: {
                    display: true,
                    text: `Marża dzienna (${selectedCurrency})`,
                },
                min: barChartMin, // Scale starts at twice the biggest loss or 0
                max: barChartMax, // Scale ends at twice the biggest profit
                ticks: {
                    callback: (value: any) => `${value} ${selectedCurrency}`, // Format y-axis as PLN values
                },
                grid: {
                    drawOnChartArea: false, // Avoid overlapping grids with the left y-axis
                },
            },
        },
    };

    return <>
        {/* @ts-ignore */}
        <Chart type="bar" style={{ maxHeight: 400 }} data={chartData} options={options} />
    </>;
};
